export enum StepChangeDirection {
  Previous = 'prev',
  Next = 'next',
}

export interface AddRequiredInfoProps {
  residentSlug?: string;
  setResidentSlug?: (id: string) => void;
  changeStep: (status: StepChangeDirection) => void;
}

export interface AddNewResidentTypes {
  full_name: string;
  bod: string;
  partner_carehome_id: string;
  profile_banner?: string;
  username?: string;
  bio: string;
  avatar?: string;
}

export interface ResidentData {
  bod: string;
  city: string;
  created_at: string;
  created_by: string;
  full_name: string;
  id: string;
  profile_banner: string;
  updated_at: string;
  updated_by: string;
  user_id: string;
  user_professional_id: number;
  visibility: boolean;
}

interface UserProfileData {
  id: string;
  acp: string;
  nhs_number: string;
  mw_number: string;
  mental_capacity: string | null;
  conversations: string | null;
  prev_self_completed_ACP: string;
  carehome_user_id: string;
  visibility: boolean;
  full_name: string;
  bod: string;
  phone: string;
  home_phone: string;
  sex: string;
  country_id: string | null;
  sub_country_id: string | null;
  bio: string;
  profile_banner: string;
  created_by: string;
  preferred_email: string;
  preferred_method_of_contact: string;
  other_contact: string;
  marital_status: string;
  prognosis: string;
  is_reside: boolean;
  current_address_type: string;
  current_address_line1: string;
  current_address_line2: string;
  current_address_city: string;
  current_address_county: string;
  current_address_postcode: string;
  alternative_address_type: string;
  alternative_address_line1: string;
  alternative_address_line2: string;
  alternative_address_city: string;
  alternative_address_county: string;
  alternative_address_postcode: string;
  gp_name: string;
  gp_practice_name: string;
  gp_address_type: string;
  gp_address_line1: string;
  gp_address_line2: string;
  gp_address_city: string;
  gp_address_county: string;
  gp_address_postcode: string;
  organ_tissues_donation: boolean;
  updated_by: string;
  created_at: string;
  updated_at: string;
  state?: string;
  is_next_of_kin: boolean;
  next_of_kin_lpa_for_health_welfare: string;
  nfk_name: string;
  nfk_phone: string;
  nfk_relationship: string;
}

export interface ResidentDetails {
  key?: string;
  profile: UserProfileData;
  preferredName?: string;
  email: string;
  id: string;
  username: string;
  slug: string;
  avatar?: string;
  nfkFullName?: string;
  nfkPhoneNumber?: string;
  nfkRelationship?: string;
  reviewDate?: string;
  is_migrated: boolean;
  care_status: string;
}

export enum ResidentPageType {
  EDIT = 'Edit',
  ADD = 'Add',
}

export type MultipleStepsAnswer =
  | string
  | [{ name: string; data?: string; id: string }];

export interface StepDetails<T> {
  key: string;
  question: string;
  answer: T;
}

export type Step4Answer = Array<{
  fullName: string;
  email?: string;
  phoneNumber?: string;
  relationship?: string;
  isAllowed?: boolean | null;
}>;

export interface PreferredPlaceWithSnomed {
  label: string;
  value: string;
  snomedCode: string | null;
}

export interface EndOfLifeCareDetails {
  summaryCoreWishes: string;
  preferredPlaceOfCareFirstChoice: PreferredPlaceWithSnomed | null;
  preferredPlaceOfCareSecondChoice: PreferredPlaceWithSnomed | null;
  preferredPlaceOfDeathFirstChoice: PreferredPlaceWithSnomed | null;
  preferredPlaceOfDeathSecondChoice: PreferredPlaceWithSnomed | null;
  hasRespectFormCompleted: string;
  respectFormFiles: FileListData[];
  hasUniversalCarePlan: string;
  isOnPalliativeCareRegister: string;
  familyAwareOfPrognosis: string;
}
export interface ThreeStepsDetails {
  step2?: StepDetails<MultipleStepsAnswer>[];
  step3?: StepDetails<MultipleStepsAnswer>[];
  step4?: EndOfLifeCareDetails;
}
export interface FileListData {
  name: string;
  data?: string;
  id: string;
}
export interface SingleFileData {
  name: string;
  data?: string;
}

export interface RadioType {
  value: string;
  label: string;
}

export enum CareStatusEnum {
  BeingCaredForByUsResident = 'beingCaredForByUsResident',
  BeingCaredForByUsCommunity = 'beingCaredForByUsCommunity',
  Discharged = 'discharged',
  HasDied = 'hasDied',
  BeingCaredForBySomeoneElse = 'beingCaredForBySomeoneElse',
}

export const statusOptions: RadioType[] = [
  {
    value: CareStatusEnum.BeingCaredForByUsResident,
    label: 'Being cared for by us (resident)',
  },
  {
    value: CareStatusEnum.BeingCaredForByUsCommunity,
    label: 'Being cared for by us (at home / in the community)',
  },
  {
    value: CareStatusEnum.Discharged,
    label: 'Discharged. Now being cared for by someone else',
  },
  {
    value: CareStatusEnum.BeingCaredForBySomeoneElse,
    label: 'Now being cared for by someone else',
  },
  { value: CareStatusEnum.HasDied, label: 'Has died' },
];

export interface Conversation {
  id: string;
  user_id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  relationship: string;
  isAllowed: boolean;
  nextOfKin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
  phoneNumber: string;
}

export interface ContactInfoType {
  fullName: string;
  email?: string;
  phoneNumber?: string;
  relationship?: string;
}

export interface LastedUpdateDateData {
  id: string;
  email: string;
  full_name: string;
  updatedAt: string;
}

export interface User {
  id: string;
  full_name: string;
  email: string;
}

export interface LogItem {
  id: string;
  updatedAt: string;
  user: User;
}

export type LogList = LogItem[];
